import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingCart, Search, MessageCircle, Package, BadgePercent } from "lucide-react";
import { cachedGet } from "@/lib/api";
import { imgVariant, imgSrcSet, bannerSrcSet } from "@/lib/img";
import { Hero } from "@/components/store/Hero";
import { ProductCard } from "@/components/store/ProductCard";
import { ReviewsSection } from "@/components/store/ReviewsSection";
import { useCategoryTree } from "@/components/store/Header";
import { useSettings } from "@/context/SettingsContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

const CATEGORY_IMAGES = {
  sofas: "https://images.unsplash.com/photo-1759722665629-29df6ee4f9a5?auto=format&fit=crop&w=600&q=80",
  "guarda-roupas": "https://images.unsplash.com/photo-1672137233327-37b0c1049e77?auto=format&fit=crop&w=600&q=80",
  camas: "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=600&q=80",
  mesas: "https://images.unsplash.com/photo-1648536474453-2b9e03f0238c?auto=format&fit=crop&w=600&q=80",
  "racks-e-paineis": "https://images.unsplash.com/photo-1738168259543-d0c58e2b91ed?auto=format&fit=crop&w=600&q=80",
  cozinha: "https://images.unsplash.com/photo-1602028915047-37269d1a73f7?auto=format&fit=crop&w=600&q=80",
};

const STEPS = [
  { icon: Search, title: "1. Escolha seus móveis", desc: "Navegue pelo catálogo e adicione ao carrinho" },
  { icon: ShoppingCart, title: "2. Finalize o pedido", desc: "Informe nome, cidade e bairro — sem pagamento online" },
  { icon: MessageCircle, title: "3. Confirme no WhatsApp", desc: "Seu pedido chega pronto no nosso atendimento" },
  { icon: Package, title: "4. Pague na entrega", desc: "PIX, dinheiro ou cartão em até 12x sem juros" },
];

const FADE_UP = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

export default function HomePage() {
  const settings = useSettings();
  const [heroBanner, setHeroBanner] = useState(null);
  const [promoBanner, setPromoBanner] = useState(null);
  const [offers, setOffers] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const categories = useCategoryTree();
  const sections = settings.home_sections || [];
  // entrada suave das seções — desligável no painel (Animações e Efeitos)
  const entranceOff = (settings.animations || {}).enabled === false || (settings.animations || {}).section_entrance === false;
  const fadeUp = entranceOff ? {} : FADE_UP;

  useEffect(() => {
    // cache leve por visita: evita disparos duplicados de rede (StrictMode/dev e re-renderizações)
    cachedGet("/banners?slot=hero").then((d) => setHeroBanner(d || [])).catch(() => {});
    cachedGet("/banners?slot=promo").then((d) => setPromoBanner((d || [])[0] || null)).catch(() => {});
    cachedGet("/products?promo=true&limit=4").then((d) => setOffers(d.items)).catch(() => {});
    cachedGet("/products?featured=true&limit=8").then((d) => setFeatured(d.items)).catch(() => {});
    cachedGet("/catalog/best-sellers?limit=4").then((d) => setBestSellers(d || [])).catch(() => {});
  }, []);

  const sectionVisible = (id) => {
    const s = sections.find((x) => x.id === id);
    return s ? s.visible !== false : true; // seção sem config = visível
  };
  const sectionTitle = (id, fallback) => {
    const s = sections.find((x) => x.id === id);
    return s?.title || fallback;
  };
  // ordem definida no painel (Editor da Home); seções desconhecidas entram ao final
  const orderedIds = sections.length
    ? [...sections.map((s) => s.id), "promo_banner"].filter((id, i, arr) => arr.indexOf(id) === i)
    : ["hero", "categories", "offers", "promo_banner", "bestsellers", "featured", "trust", "reviews"];

  const renderSection = (id) => {
    if (!sectionVisible(id)) return null;
    switch (id) {
      case "hero":
        return <Hero key={id} banners={heroBanner} />;

      case "categories":
        return (
          <section key={id} className="container-store py-10" data-testid="home-categories">
            <motion.div {...fadeUp}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                {sectionTitle("categories", "Compre por ambiente")}
              </h2>
              <p className="text-gray-500 mt-1">Móveis para quarto, sala, cozinha e muito mais</p>
            </motion.div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {categories.map((cat, i) => (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link
                    to={`/categoria/${cat.slug}`}
                    className="group block bg-white border border-gray-100 rounded-xl overflow-hidden card-hover"
                    data-testid={`home-category-${cat.slug}`}
                  >
                    <div className="aspect-square bg-gray-50 overflow-hidden">
                      <img
                        src={imgVariant(cat.image || CATEGORY_IMAGES[cat.slug], "card") || "/assets/logo.png"}
                        srcSet={imgSrcSet(cat.image || CATEGORY_IMAGES[cat.slug])}
                        sizes="(max-width: 640px) 50vw, 16vw"
                        alt={cat.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <p className="py-3.5 text-center text-base font-semibold text-gray-800 group-hover:text-brand transition-colors">
                      {cat.name}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        );

      case "offers":
        if (offers.length === 0) return null;
        return (
          <section key={id} className="bg-brand-light/60 py-12" data-testid="home-offers">
            <div className="container-store">
              <motion.div {...fadeUp} className="flex items-end justify-between gap-4">
                <div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-yellow-600">
                    <BadgePercent className="w-4 h-4" /> Ofertas
                  </span>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mt-1">
                    {sectionTitle("offers", "Promoções da semana")}
                  </h2>
                </div>
                <Link to="/produtos?promo=1">
                  <Button variant="outline" className="rounded-full" data-testid="offers-see-all">
                    Ver todas <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {offers.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          </section>
        );

      case "promo_banner":
        if (!promoBanner) return null;
        return (
          <section key={id} className="container-store py-12" data-testid="home-promo-banner">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-2xl bg-brand min-h-[220px] flex items-center"
            >
              <picture>
                {promoBanner.image_mobile && (
                  <source media="(max-width: 640px)" srcSet={promoBanner.image_mobile} />
                )}
                <img
                  src={imgVariant(promoBanner.image_url, "full")}
                  srcSet={bannerSrcSet(promoBanner.image_url)}
                  sizes="(max-width: 1024px) 100vw, 1200px"
                  alt=""
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-r from-brand via-brand/85 to-brand/40" />
              <div className="relative p-8 sm:p-12 max-w-lg">
                <h3 className="font-heading text-2xl sm:text-4xl font-bold text-white tracking-tight">
                  {promoBanner.title}
                </h3>
                <p className="mt-2 text-blue-100 text-sm sm:text-base">{promoBanner.subtitle}</p>
                <Link to={promoBanner.cta_link || "/produtos"}>
                  <Button
                    variant="secondary"
                    className="mt-6 rounded-full px-6 font-semibold active:scale-95 transition-transform"
                    data-testid="promo-banner-cta"
                  >
                    {promoBanner.cta_text} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              {promoBanner.show_avatar && (
                <img
                  src="/assets/avatar.webp"
                  alt="Mascote HG Móveis"
                  className="absolute bottom-0 right-2 sm:right-8 h-36 sm:h-56 lg:h-64 object-contain object-bottom drop-shadow-xl pointer-events-none"
                  data-testid="promo-avatar"
                />
              )}
            </motion.div>
          </section>
        );

      case "bestsellers":
        if (bestSellers.length === 0) return null;
        return (
          <section key={id} className="container-store py-10" data-testid="home-bestsellers">
            <motion.div {...fadeUp}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                {sectionTitle("bestsellers", "Mais Vendidos")}
              </h2>
              <p className="text-gray-500 mt-1">Os produtos mais pedidos por quem compra na HG Móveis</p>
            </motion.div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {bestSellers.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        );

      case "featured":
        if (featured.length === 0) return null;
        return (
          <section key={id} className="container-store py-10" data-testid="home-featured">
            <motion.div {...fadeUp} className="flex items-end justify-between gap-4">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  {sectionTitle("featured", "Produtos em destaque")}
                </h2>
                <p className="text-gray-500 mt-1">Os mais procurados da nossa loja</p>
              </div>
              <Link to="/produtos">
                <Button variant="outline" className="rounded-full" data-testid="featured-see-all">
                  Ver catálogo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {featured.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        );

      case "trust":
        return (
          <section key={id} className="bg-white border-t border-gray-100 py-14" data-testid="home-how-it-works">
            <div className="container-store">
              <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  Como comprar na HG Móveis
                </h2>
                <p className="text-gray-500 mt-2">Simples, rápido e seguro — você só paga quando receber</p>
              </motion.div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {STEPS.map(({ icon: Icon, title, desc }, i) => (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="text-center"
                    data-testid={`step-${i + 1}`}
                  >
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-brand-light text-brand flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-gray-900">{title}</h3>
                    <p className="mt-1 text-sm text-gray-500">{desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

      case "catalog":
        return (
          <section key={id} className="container-store py-12" data-testid="home-catalog">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-2xl bg-slate-900 px-8 py-10 sm:px-12 sm:py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
            >
              <div className="max-w-xl">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  {sectionTitle("catalog", "Catálogo HG Móveis")}
                </h2>
                <p className="mt-2 text-slate-300 text-sm sm:text-base">
                  Todos os produtos organizados por categoria, com fotos, preços e estoque sempre atualizados.
                  Compartilhe o link pelo WhatsApp — quem abre vê as informações do momento.
                </p>
              </div>
              <Link to="/catalogo" className="shrink-0">
                <Button
                  size="lg"
                  className="rounded-full px-7 bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold uppercase tracking-wide active:scale-95 transition-transform"
                  data-testid="home-catalog-cta"
                >
                  Ver catálogo completo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </section>
        );

      case "reviews":
        return <ReviewsSection key={id} title={sectionTitle("reviews", "O que nossos clientes dizem")} />;

      default:
        return null;
    }
  };

  return (
    <div data-testid="home-page">
      <Seo
        title={settings.seo?.home_title || "HG Móveis | Sofás, Guarda-Roupas, Camas e Cozinhas no RJ"}
        description={settings.seo?.home_description || "Compre móveis na HG Móveis! Sofás, guarda-roupas, camas, colchões, cozinhas e muito mais. Atendemos regiões selecionadas do RJ, com pagamento na entrega."}
        canonical="/"
        ogImage="/assets/og-image.png"
      />
      {orderedIds.map(renderSection)}
    </div>
  );
}
